<script>
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import WootButton from 'dashboard/components-next/button/Button.vue';
import inboxesAPI from 'dashboard/api/inboxes';

export default {
  components: {
    SettingsSection,
    WootButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      instanceSettings: {
        reject_call: false,
        groupsIgnore: true,
        alwaysOnline: false,
        readMessages: false,
        readStatus: false,
        syncFullHistory: false,
      },
      // Estados para reconexión
      connectionStatus: 'unknown', // unknown, connected, disconnected
      isCheckingConnection: false,
      showReconnectModal: false,
      qrCode: '',
      isGeneratingQr: false,
      connectionCheckInterval: null,
      reconnectionInterval: null,
      // Estado para restart
      isRestarting: false,
      // Estado para renovación de instancia
      isRenewingInstance: false,
      showRenewProgress: false,
      renewProgressSteps: [],
      // Flag para evitar múltiples alertas de reconexión
      hasShownReconnectSuccess: false,
      // Estado para descarga de contactos
      isDownloadingContacts: false,
    };
  },
  computed: {
    isEvolutionAPIInbox() {
      // Método 1: Por api_provider (para bandejas nuevas)
      if (this.inbox?.additional_attributes?.api_provider === 'evolution_api') {
        return true;
      }

      // Método 2: Por webhook_url que contenga un patrón Evolution API (para bandejas existentes)
      if (
        this.inbox?.webhook_url &&
        this.inbox.channel_type === 'Channel::Api'
      ) {
        // Si el webhook_url contiene chatwoot/webhook/ es probablemente Evolution API
        return this.inbox.webhook_url.includes('/chatwoot/webhook/');
      }

      return false;
    },
    evolutionApiUrl() {
      // Prioridad 1: URL configurada explícitamente en el inbox
      if (this.inbox?.additional_attributes?.evolution_api_url) {
        return this.inbox.additional_attributes.evolution_api_url;
      }

      // Prioridad 2: Buscar en la nueva configuración EVOLUTION_API_SERVERS
      const servers = window.globalConfig?.EVOLUTION_API_SERVERS;
      if (servers) {
        const defaultKey = servers.default;
        const defaultServer = defaultKey ? servers[defaultKey] : null;
        if (defaultServer?.url) {
          return defaultServer.url;
        }
      }

      // Prioridad 3: Fallback a la configuración antigua
      return window.globalConfig?.EVOLUTION_API_URL;
    },
    evolutionApiKey() {
      // Prioridad 1: Key configurada explícitamente en el inbox
      if (this.inbox?.additional_attributes?.evolution_api_key) {
        return this.inbox.additional_attributes.evolution_api_key;
      }

      // Prioridad 2: Buscar en la nueva configuración EVOLUTION_API_SERVERS
      const servers = window.globalConfig?.EVOLUTION_API_SERVERS;
      if (servers) {
        const defaultKey = servers.default;
        const defaultServer = defaultKey ? servers[defaultKey] : null;
        if (defaultServer?.key) {
          return defaultServer.key;
        }
      }

      // Prioridad 3: Fallback a la configuración antigua
      return window.globalConfig?.EVOLUTION_API_KEY;
    },
    instanceName() {
      // Prioridad 1: nombre configurado explícitamente
      if (this.inbox?.additional_attributes?.evolution_instance_name) {
        return this.inbox.additional_attributes.evolution_instance_name;
      }

      // Prioridad 2: extraer desde webhook URL
      if (this.inbox?.webhook_url) {
        // Buscar patrón /chatwoot/webhook/{instanceName}
        const webhookMatch = this.inbox.webhook_url.match(
          /\/chatwoot\/webhook\/([^/?]+)/
        );
        if (webhookMatch && webhookMatch[1]) {
          return webhookMatch[1];
        }

        // Fallback: tomar el último segmento de la URL
        const urlParts = this.inbox.webhook_url.split('/');
        const lastPart = urlParts[urlParts.length - 1];
        if (lastPart && lastPart !== 'webhook') {
          return lastPart;
        }
      }

      // Prioridad 3: fallback a name o phone_number
      return this.inbox?.name || this.inbox?.phone_number;
    },
    renewProgressPercentage() {
      if (!this.renewProgressSteps.length) {
        return 0;
      }

      const stepProgress = 100 / this.renewProgressSteps.length;
      const completedCount = this.renewProgressSteps.filter(
        step => step.status === 'completed'
      ).length;
      const hasInProgress = this.renewProgressSteps.some(
        step => step.status === 'in_progress'
      );

      return Math.min(
        completedCount * stepProgress +
          (hasInProgress ? stepProgress * 0.6 : 0),
        100
      );
    },
  },
  mounted() {
    if (this.isEvolutionAPIInbox) {
      this.loadInstanceSettings();
      this.checkConnectionStatus();
      // Verificar conexión cada 30 segundos
      this.startConnectionPolling();
    }
  },
  beforeUnmount() {
    this.stopConnectionPolling();
  },
  methods: {
    async loadInstanceSettings() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        return;
      }

      this.isLoading = true;
      try {
        const response = await fetch(
          `${this.evolutionApiUrl}/settings/find/${this.instanceName}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error getting instance settings: ${response.statusText}`
          );
        }

        const result = await response.json();

        // Actualizar las configuraciones con los valores obtenidos
        // Manejar diferentes estructuras de respuesta de la API
        let settings = result.settings || result;

        if (settings) {
          // Helper function para manejar valores booleanos correctamente
          const getValue = (primary, secondary, defaultValue) => {
            if (primary !== undefined) return primary;
            if (secondary !== undefined) return secondary;
            return defaultValue;
          };

          this.instanceSettings = {
            reject_call: getValue(
              settings.reject_call,
              settings.rejectCall,
              false
            ),
            groupsIgnore: getValue(
              settings.groupsIgnore,
              settings.groups_ignore,
              true
            ),
            alwaysOnline: getValue(
              settings.alwaysOnline,
              settings.always_online,
              false
            ),
            readMessages: getValue(
              settings.readMessages,
              settings.read_messages,
              false
            ),
            readStatus: getValue(
              settings.readStatus,
              settings.read_status,
              false
            ),
            syncFullHistory: getValue(
              settings.syncFullHistory,
              settings.sync_full_history,
              false
            ),
          };
        }
      } catch (error) {
        useAlert(
          `${this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.LOAD_ERROR')}: ${error.message}`
        );
      } finally {
        this.isLoading = false;
      }
    },

    async saveInstanceSettings() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INCOMPLETE_CONFIG'
          )
        );
        return;
      }

      this.isSaving = true;
      try {
        // Convertir el formato para que coincida con la API de Evolution
        const evolutionApiSettings = {
          rejectCall: this.instanceSettings.reject_call,
          groupsIgnore: this.instanceSettings.groupsIgnore,
          alwaysOnline: this.instanceSettings.alwaysOnline,
          readMessages: this.instanceSettings.readMessages,
          readStatus: this.instanceSettings.readStatus,
          syncFullHistory: this.instanceSettings.syncFullHistory,
        };

        const response = await fetch(
          `${this.evolutionApiUrl}/settings/set/${this.instanceName}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
            body: JSON.stringify(evolutionApiSettings),
          }
        );

        if (!response.ok) {
          throw new Error(
            `Error updating instance settings: ${response.statusText}`
          );
        }

        useAlert(
          this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.SAVE_SUCCESS')
        );

        // Recargar las configuraciones desde Evolution API para mostrar los valores actualizados
        await this.loadInstanceSettings();
      } catch (error) {
        useAlert(
          `${this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.SAVE_ERROR')}: ${error.message}`
        );
      } finally {
        this.isSaving = false;
      }
    },

    // Métodos para verificar y manejar la conexión
    async checkConnectionStatus() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        return;
      }

      this.isCheckingConnection = true;
      try {
        const response = await fetch(
          `${this.evolutionApiUrl}/instance/connect/${this.instanceName}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          // Verificar diferentes formatos de respuesta
          const isConnected =
            result.instance?.state === 'open' ||
            result.state === 'open' ||
            result.connected === true;

          this.connectionStatus = isConnected ? 'connected' : 'disconnected';
        } else {
          this.connectionStatus = 'disconnected';
        }
      } catch (error) {
        // Error checking connection
        this.connectionStatus = 'disconnected';
      } finally {
        this.isCheckingConnection = false;
      }
    },

    async restartInstance() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        // Error: Configuración incompleta para reiniciar instancia
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INCOMPLETE_CONFIG'
          )
        );
        return;
      }

      // Iniciando reinicio de instancia
      this.isRestarting = true;

      try {
        const response = await fetch(
          `${this.evolutionApiUrl}/instance/restart/${this.instanceName}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
          }
        );

        if (response.ok) {
          await response.json();
          // Instancia reiniciada exitosamente

          useAlert(
            this.$t(
              'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RESTART_SUCCESS'
            )
          );

          // Verificar el estado de conexión después de un breve delay
          setTimeout(() => {
            this.checkConnectionStatus();
          }, 2000);
        } else if (response.status === 404) {
          // Error 404: Instancia no encontrada
          useAlert(
            this.$t(
              'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INSTANCE_NOT_FOUND'
            )
          );
        } else {
          const errorText = await response.text();
          // Error en reinicio de instancia
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
      } catch (error) {
        // Error al reiniciar instancia
        useAlert(
          `${this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RESTART_ERROR')}: ${error.message}`
        );
      } finally {
        this.isRestarting = false;
      }
    },

    async renewInstance() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INCOMPLETE_CONFIG'
          )
        );
        return;
      }

      const confirmed = await this.$refs.renewConfirmDialog.showConfirmation();
      if (!confirmed) {
        return;
      }

      const currentUser = this.$store.getters.getCurrentUser;
      const userToken = currentUser?.access_token;

      if (!userToken) {
        useAlert(
          this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.TOKEN_REQUIRED')
        );
        return;
      }

      if (!this.inbox?.id) {
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INCOMPLETE_CONFIG'
          )
        );
        return;
      }

      this.isRenewingInstance = true;
      this.initializeRenewProgress();
      this.showRenewProgress = true;

      try {
        this.setRenewStepStatus('delete', 'in_progress');
        await this.sleep(800);
        this.setRenewStepStatus('delete', 'completed');

        this.setRenewStepStatus('create', 'in_progress');
        await this.sleep(900);
        this.setRenewStepStatus('create', 'completed');

        this.setRenewStepStatus('configure', 'in_progress');
        await inboxesAPI.renewEvolutionInstance(this.inbox.id, {
          token: userToken,
          chatwoot_url: window.location.origin,
          reopen_conversation: true,
          conversation_pending: false,
          import_contacts: false,
          import_messages: false,
          days_limit_import_messages: 0,
        });

        await this.sleep(700);
        this.setRenewStepStatus('configure', 'completed');

        useAlert(
          this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RENEW_SUCCESS')
        );

        await this.sleep(400);
        this.showRenewProgress = false;
        await this.checkConnectionStatus();
        await this.startReconnection();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || error?.message || 'Error desconocido';

        this.markCurrentRenewStepAsError();
        this.showRenewProgress = false;
        useAlert(
          `${this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RENEW_ERROR')}: ${errorMessage}`
        );
      } finally {
        this.isRenewingInstance = false;
      }
    },

    initializeRenewProgress() {
      this.renewProgressSteps = [
        {
          id: 'delete',
          label: this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.STEP_DELETE'
          ),
          status: 'pending',
        },
        {
          id: 'create',
          label: this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.STEP_CREATE'
          ),
          status: 'pending',
        },
        {
          id: 'configure',
          label: this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.STEP_CONFIGURE'
          ),
          status: 'pending',
        },
      ];
    },

    setRenewStepStatus(stepId, status) {
      this.renewProgressSteps = this.renewProgressSteps.map(step =>
        step.id === stepId ? { ...step, status } : step
      );
    },

    markCurrentRenewStepAsError() {
      const currentStep = this.renewProgressSteps.find(
        step => step.status === 'in_progress'
      );

      if (!currentStep) {
        return;
      }

      this.setRenewStepStatus(currentStep.id, 'error');
    },

    sleep(ms) {
      return new Promise(resolve => {
        setTimeout(() => {
          resolve();
        }, ms);
      });
    },

    getRenewStepStatusLabel(status) {
      if (status === 'completed') {
        return this.$t(
          'INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.STATUS_COMPLETED'
        );
      }

      if (status === 'in_progress') {
        return this.$t(
          'INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.STATUS_IN_PROGRESS'
        );
      }

      if (status === 'error') {
        return this.$t(
          'INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.STATUS_ERROR'
        );
      }

      return this.$t(
        'INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.STATUS_PENDING'
      );
    },

    startConnectionPolling() {
      this.connectionCheckInterval = setInterval(() => {
        this.checkConnectionStatus();
      }, 30000); // Verificar cada 30 segundos
    },

    stopConnectionPolling() {
      if (this.connectionCheckInterval) {
        clearInterval(this.connectionCheckInterval);
        this.connectionCheckInterval = null;
      }
    },

    async startReconnection() {
      this.hasShownReconnectSuccess = false; // Reset flag
      this.showReconnectModal = true;
      await this.generateQrCode();
    },

    async generateQrCode() {
      this.isGeneratingQr = true;
      this.qrCode = '';

      try {
        // Intentar múltiples endpoints para generar QR
        let result = null;

        // Endpoint principal
        try {
          const response = await fetch(
            `${this.evolutionApiUrl}/instance/connect/${this.instanceName}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                apikey: this.evolutionApiKey,
              },
            }
          );

          if (response.ok) {
            result = await response.json();
          }
        } catch (error) {
          // Intentar endpoint alternativo
          const response2 = await fetch(
            `${this.evolutionApiUrl}/instance/qr/${this.instanceName}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                apikey: this.evolutionApiKey,
              },
            }
          );

          if (response2.ok) {
            result = await response2.json();
          } else {
            throw new Error(`Error generating QR: ${response2.statusText}`);
          }
        }

        if (!result) {
          throw new Error('No response from Evolution API');
        }

        // Verificar diferentes posibles estructuras de respuesta
        let qrData = null;

        // Buscar el QR en diferentes campos posibles
        if (result.base64) {
          qrData = result.base64;
        } else if (result.code) {
          qrData = result.code;
        } else if (result.qrcode) {
          qrData = result.qrcode;
        } else if (result.qr) {
          qrData = result.qr;
        } else if (result.data) {
          qrData = result.data;
        } else if (result.qr_code) {
          qrData = result.qr_code;
        } else if (result.image) {
          qrData = result.image;
        }

        if (qrData) {
          // Limpiar el string QR de caracteres innecesarios
          qrData = qrData.toString().trim();

          // Si ya viene con el prefijo data:image, usarlo tal como está
          if (qrData.startsWith('data:image/')) {
            this.qrCode = qrData;
          } else if (
            qrData.startsWith('iVBOR') ||
            qrData.startsWith('/9j/') ||
            qrData.match(/^[A-Za-z0-9+/]+=*$/)
          ) {
            // Si parece ser base64 válido
            this.qrCode = `data:image/png;base64,${qrData}`;
          } else {
            // Como último recurso, intentar como URL directa
            this.qrCode = qrData;
          }

          this.startReconnectionPolling();
        } else {
          throw new Error('QR code data not found in API response');
        }
      } catch (error) {
        useAlert(`Error generating QR code: ${error.message}`);
        this.closeReconnectModal();
      } finally {
        this.isGeneratingQr = false;
      }
    },

    startReconnectionPolling() {
      // Verificar conexión cada 3 segundos durante la reconexión
      this.reconnectionInterval = setInterval(async () => {
        await this.checkConnectionStatus();
        if (this.connectionStatus === 'connected') {
          this.onReconnectionSuccess();
        }
      }, 3000);
    },

    stopReconnectionPolling() {
      if (this.reconnectionInterval) {
        clearInterval(this.reconnectionInterval);
        this.reconnectionInterval = null;
      }
    },

    onReconnectionSuccess() {
      if (!this.hasShownReconnectSuccess) {
        this.hasShownReconnectSuccess = true;
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RECONNECT_SUCCESS'
          )
        );
      }
      this.stopReconnectionPolling();
      this.closeReconnectModal();
    },

    closeReconnectModal() {
      this.showReconnectModal = false;
      this.qrCode = '';
      this.isGeneratingQr = false;
      this.stopReconnectionPolling();
    },

    async refreshQrCode() {
      await this.generateQrCode();
    },

    onQrImageError() {
      // Error loading QR image
      useAlert('Error loading QR code image. Please try refreshing.');
    },

    onQrImageLoad() {
      // QR code cargado correctamente
    },

    async downloadContactsList() {
      if (
        !this.evolutionApiUrl ||
        !this.evolutionApiKey ||
        !this.instanceName
      ) {
        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.INCOMPLETE_CONFIG'
          )
        );
        return;
      }

      this.isDownloadingContacts = true;
      try {
        const response = await fetch(
          `${this.evolutionApiUrl}/chat/findContacts/${this.instanceName}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              apikey: this.evolutionApiKey,
            },
            body: JSON.stringify({
              where: {
                isGroup: false, // Solo contactos individuales
              },
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contacts = await response.json();

        // Filtrar contactos válidos (con nombre y número @s.whatsapp.net)
        const validContacts = contacts.filter(contact => {
          const name = contact.pushName || contact.name;
          const remoteJid = contact.remoteJid || contact.id || '';
          const isValidPhone = remoteJid.includes('@s.whatsapp.net');
          return name && name.trim() && isValidPhone;
        });

        if (validContacts.length === 0) {
          useAlert(
            this.$t(
              'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.NO_CONTACTS_FOUND'
            )
          );
          return;
        }

        // Ordenar alfabéticamente por nombre
        validContacts.sort((a, b) => {
          const nameA = (a.pushName || a.name || '').toLowerCase();
          const nameB = (b.pushName || b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });

        // Generar CSV
        const csvContent = this.generateContactsCSV(validContacts);

        // Descargar archivo
        const blob = new Blob(['\uFEFF' + csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const timestamp = new Date().toISOString().split('T')[0];
        link.download = `contacts_${this.instanceName}_${timestamp}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);

        useAlert(
          this.$t(
            'INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.CONTACTS_DOWNLOAD_SUCCESS',
            { count: validContacts.length }
          )
        );
      } catch (error) {
        useAlert(
          `${this.$t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.CONTACTS_DOWNLOAD_ERROR')}: ${error.message}`
        );
      } finally {
        this.isDownloadingContacts = false;
      }
    },

    generateContactsCSV(contacts) {
      // BOM para UTF-8 con Excel
      // Punto y coma como separador para Excel en español
      let csv = 'phone_number;name\n';

      contacts.forEach(contact => {
        const name = (contact.pushName || contact.name || '').replace(
          /;/g,
          ','
        ); // Reemplazar punto y coma
        const remoteJid = contact.remoteJid || contact.id;
        const phone = remoteJid.split('@')[0]; // Extraer número

        // Validar que sea un número válido (10-15 dígitos)
        if (phone && /^\d{10,15}$/.test(phone)) {
          csv += `${phone};${name}\n`;
        }
      });

      return csv;
    },
  },
};
</script>

<template>
  <div v-if="isEvolutionAPIInbox">
    <SettingsSection
      :title="$t('INBOX_MGMT.INSTANCE_CONFIGURATION.TITLE')"
      :sub-title="$t('INBOX_MGMT.INSTANCE_CONFIGURATION.DESCRIPTION')"
    >
      <div v-if="isLoading" class="flex justify-center py-8">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-woot-500"
        />
      </div>

      <div v-else class="space-y-6">
        <div v-if="showRenewProgress" class="qr-section">
          <h3 class="qr-title">
            {{ $t('INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.TITLE') }}
          </h3>
          <p class="config-description mb-4">
            {{
              $t('INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_PROGRESS.SUBTITLE')
            }}
          </p>

          <div class="relative px-2 pt-2">
            <div class="absolute left-10 right-10 top-7 h-0.5 bg-slate-600" />
            <div
              class="absolute left-10 top-7 h-0.5 bg-woot-500 transition-all duration-500 ease-out"
              :style="{
                width: `calc((100% - 5rem) * ${renewProgressPercentage / 100})`,
              }"
            />

            <div class="relative grid grid-cols-3 gap-3">
              <div
                v-for="step in renewProgressSteps"
                :key="step.id"
                class="text-center"
              >
                <div
                  class="relative z-10 mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full border-2 bg-slate-800 text-sm font-semibold"
                  :class="{
                    'border-emerald-400 text-emerald-300':
                      step.status === 'completed',
                    'border-red-400 text-red-300': step.status === 'error',
                    'border-woot-500 text-woot-300 animate-pulse':
                      step.status === 'in_progress',
                    'border-slate-500 text-slate-300':
                      step.status === 'pending',
                  }"
                >
                  <svg
                    v-if="step.status === 'completed'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="h-4 w-4"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <svg
                    v-else-if="step.id === 'delete'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="h-4 w-4"
                  >
                    <path d="M3 6h18" />
                    <path d="M8 6V4h8v2" />
                    <path d="M19 6l-1 14H6L5 6" />
                    <path d="M10 11v6" />
                    <path d="M14 11v6" />
                  </svg>
                  <svg
                    v-else-if="step.id === 'create'"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="h-4 w-4"
                  >
                    <path d="M12 5v14" />
                    <path d="M5 12h14" />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    class="h-4 w-4"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path
                      d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-.4-1.1 1.7 1.7 0 0 0-1-.6 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H2.8a2 2 0 1 1 0-4H2.9A1.7 1.7 0 0 0 4 9.2a1.7 1.7 0 0 0 .6-1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V2.8a2 2 0 1 1 4 0V2.9a1.7 1.7 0 0 0 .4 1.1 1.7 1.7 0 0 0 1 .6 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.27.3.47.64.6 1 .09.33.13.68.1 1.02a2 2 0 0 1 0 2c.03.34-.01.69-.1 1.02-.13.36-.33.7-.6 1Z"
                    />
                  </svg>
                </div>

                <div class="mb-1">
                  <span class="config-title mb-0">{{ step.label }}</span>
                </div>
                <span
                  class="config-description"
                  :class="{
                    '!text-emerald-300': step.status === 'completed',
                  }"
                >
                  {{ getRenewStepStatusLabel(step.status) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <template v-else>
          <!-- Estado de Conexión -->
          <div class="config-section">
            <div class="flex items-center space-x-3">
              <div class="flex-shrink-0">
                <div
                  v-if="connectionStatus === 'connected'"
                  class="connection-indicator connection-indicator--connected"
                />
                <div
                  v-else-if="connectionStatus === 'disconnected'"
                  class="connection-indicator connection-indicator--disconnected"
                />
                <div
                  v-else
                  class="connection-indicator connection-indicator--checking"
                />
              </div>
              <div>
                <h4 class="config-title">
                  {{
                    connectionStatus === 'connected'
                      ? $t(
                          'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.CONNECTED'
                        )
                      : connectionStatus === 'disconnected'
                        ? $t(
                            'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.DISCONNECTED'
                          )
                        : $t(
                            'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.CHECKING'
                          )
                  }}
                </h4>
                <p
                  v-if="connectionStatus === 'disconnected'"
                  class="error-text"
                >
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.NEEDS_RECONNECTION'
                    )
                  }}
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button
                v-if="connectionStatus === 'disconnected'"
                type="button"
                class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                @click="startReconnection"
              >
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.RECONNECT'
                  )
                }}
              </button>
              <button
                type="button"
                class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-woot-500 hover:bg-woot-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-woot-500 disabled:opacity-50"
                :disabled="isCheckingConnection"
                @click="checkConnectionStatus"
              >
                <div
                  v-if="isCheckingConnection"
                  class="spinner spinner--small mr-1"
                />
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.CHECK_STATUS'
                  )
                }}
              </button>
              <button
                v-if="connectionStatus === 'connected'"
                type="button"
                class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm border border-transparent"
                :disabled="isRestarting"
                @click="restartInstance"
              >
                <div v-if="isRestarting" class="spinner spinner--small mr-2" />
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.RESTART'
                  )
                }}
              </button>
              <WootButton
                v-if="connectionStatus === 'connected'"
                color="blue"
                size="large"
                icon="i-lucide-download"
                class="!px-4 !py-2"
                :is-loading="isDownloadingContacts"
                :disabled="isDownloadingContacts"
                @click="downloadContactsList"
              >
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.DOWNLOAD_CONTACTS.BUTTON'
                  )
                }}
              </WootButton>
            </div>
          </div>

          <div
            v-if="!showReconnectModal"
            class="config-section renew-instance-section border-t border-n-weak pt-4"
          >
            <div class="flex-1">
              <h4 class="config-title">
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_INSTANCE.TITLE')
                }}
              </h4>
              <p class="config-description">
                {{
                  $t(
                    'INBOX_MGMT.INSTANCE_CONFIGURATION.RENEW_INSTANCE.DESCRIPTION'
                  )
                }}
              </p>
            </div>
            <button
              type="button"
              class="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white shadow-sm border border-transparent renew-instance-button disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isRenewingInstance"
              @click="renewInstance"
            >
              <div
                v-if="isRenewingInstance"
                class="spinner spinner--small mr-2"
              />
              {{
                isRenewingInstance
                  ? $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.RENEWING_INSTANCE'
                    )
                  : $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.RENEW_INSTANCE'
                    )
              }}
            </button>
          </div>

          <!-- Sección de reconexión QR (aparece debajo del estado) -->
          <div v-if="showReconnectModal" class="qr-section">
            <h3 class="qr-title">
              {{
                $t(
                  'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.RECONNECT'
                )
              }}
            </h3>

            <!-- Estado: Generando QR -->
            <div v-if="isGeneratingQr" class="text-center py-8">
              <div class="spinner spinner--xl mx-auto mb-4" />
              <p class="generating-text">
                {{
                  $t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.GENERATING_QR')
                }}
              </p>
            </div>

            <!-- QR Code -->
            <div v-else-if="qrCode" class="text-center">
              <div class="mb-6">
                <img
                  :src="qrCode"
                  alt="QR Code"
                  class="qr-image"
                  @error="onQrImageError"
                  @load="onQrImageLoad"
                />
              </div>
              <p class="qr-scan-text">
                {{ $t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.SCAN_QR') }}
              </p>
              <div class="qr-instructions">
                <p>
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.QR_INSTRUCTIONS.STEP_1'
                    )
                  }}
                </p>
                <p>
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.QR_INSTRUCTIONS.STEP_2'
                    )
                  }}
                </p>
                <p>
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.QR_INSTRUCTIONS.STEP_3'
                    )
                  }}
                </p>
                <p>
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.QR_INSTRUCTIONS.STEP_4'
                    )
                  }}
                </p>
              </div>

              <!-- Botones de acción -->
              <div class="flex justify-center space-x-3">
                <button
                  type="button"
                  class="inline-flex items-center px-4 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  @click="refreshQrCode"
                >
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.REFRESH_QR'
                    )
                  }}
                </button>
                <button
                  type="button"
                  class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="closeReconnectModal"
                >
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.CONNECTION_STATUS.CANCEL'
                    )
                  }}
                </button>
              </div>
            </div>
          </div>

          <!-- Configuraciones de instancia (solo se muestran cuando NO está en modo reconexión) -->
          <template v-else>
            <!-- Reject Calls -->
            <div class="config-section">
              <div class="flex-1">
                <h4 class="config-title">
                  {{
                    $t('INBOX_MGMT.INSTANCE_CONFIGURATION.REJECT_CALLS.TITLE')
                  }}
                </h4>
                <p class="config-description">
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.REJECT_CALLS.DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="instanceSettings.reject_call"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
                />
              </label>
            </div>

            <!-- Groups Ignore -->
            <div class="config-section">
              <div class="flex-1">
                <h4 class="config-title">
                  {{
                    $t('INBOX_MGMT.INSTANCE_CONFIGURATION.GROUPS_IGNORE.TITLE')
                  }}
                </h4>
                <p class="config-description">
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.GROUPS_IGNORE.DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="instanceSettings.groupsIgnore"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
                />
              </label>
            </div>

            <!-- Always Online -->
            <div class="config-section">
              <div class="flex-1">
                <h4 class="config-title">
                  {{
                    $t('INBOX_MGMT.INSTANCE_CONFIGURATION.ALWAYS_ONLINE.TITLE')
                  }}
                </h4>
                <p class="config-description">
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.ALWAYS_ONLINE.DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="instanceSettings.alwaysOnline"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
                />
              </label>
            </div>

            <!-- Read Messages -->
            <div class="config-section">
              <div class="flex-1">
                <h4 class="config-title">
                  {{
                    $t('INBOX_MGMT.INSTANCE_CONFIGURATION.READ_MESSAGES.TITLE')
                  }}
                </h4>
                <p class="config-description">
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.READ_MESSAGES.DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="instanceSettings.readMessages"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
                />
              </label>
            </div>

            <!-- Read Status -->
            <div class="config-section">
              <div class="flex-1">
                <h4 class="config-title">
                  {{
                    $t('INBOX_MGMT.INSTANCE_CONFIGURATION.READ_STATUS.TITLE')
                  }}
                </h4>
                <p class="config-description">
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.READ_STATUS.DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="instanceSettings.readStatus"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
                />
              </label>
            </div>

            <!-- Sync Full History -->
            <div class="config-section">
              <div class="flex-1">
                <h4 class="config-title">
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.SYNC_FULL_HISTORY.TITLE'
                    )
                  }}
                </h4>
                <p class="config-description">
                  {{
                    $t(
                      'INBOX_MGMT.INSTANCE_CONFIGURATION.SYNC_FULL_HISTORY.DESCRIPTION'
                    )
                  }}
                </p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input
                  v-model="instanceSettings.syncFullHistory"
                  type="checkbox"
                  class="sr-only peer"
                />
                <div
                  class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-woot-500"
                />
              </label>
            </div>

            <!-- Save Button -->
            <div class="pt-6 border-t border-gray-200">
              <button
                type="button"
                class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-woot-500 hover:bg-woot-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-woot-500 disabled:opacity-50"
                :disabled="isSaving"
                @click="saveInstanceSettings"
              >
                <div v-if="isSaving" class="spinner spinner--medium mr-2" />
                {{
                  isSaving
                    ? $t('INBOX_MGMT.INSTANCE_CONFIGURATION.SAVING')
                    : $t('INBOX_MGMT.INSTANCE_CONFIGURATION.SAVE')
                }}
              </button>
            </div>
          </template>
        </template>
      </div>
    </SettingsSection>
  </div>

  <!-- Fallback for non-Evolution API inboxes
  - informacion -->
  <div v-else>
    <SettingsSection
      :title="$t('INBOX_MGMT.INSTANCE_CONFIGURATION.TITLE')"
      :sub-title="$t('INBOX_MGMT.INSTANCE_CONFIGURATION.NOT_AVAILABLE')"
    >
      <div class="text-center py-8">
        <p class="fallback-text">
          {{ $t('INBOX_MGMT.INSTANCE_CONFIGURATION.ONLY_EVOLUTION_API') }}
        </p>
      </div>
    </SettingsSection>
  </div>

  <woot-confirm-modal
    ref="renewConfirmDialog"
    :title="
      $t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RENEW_CONFIRM_TITLE')
    "
    :description="
      $t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RENEW_CONFIRM')
    "
    :confirm-label="
      $t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RENEW_CONFIRM_ACCEPT')
    "
    :cancel-label="
      $t('INBOX_MGMT.INSTANCE_CONFIGURATION.MESSAGES.RENEW_CONFIRM_CANCEL')
    "
  />
</template>
